import { createQuestionsColumns } from "./-components/review-questions-columns"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation } from "convex/react"
import { z } from "zod"
import { ChevronLeft, Check, X, AlertTriangle } from "lucide-react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { DataTable } from "@/components/ui/data-table"
import { Badge } from "@/components/ui/badge"
import { SafeHtml } from "@/components/ui/safe-html"
import { useMemo } from "react"
import { toast } from "sonner"

const questionsReviewSearchSchema = z.object({
  quizstates: z.string().default(""),
})

export const Route = createFileRoute(
  "/(admin)/admin/quizzes/$quizId_/questions"
)({
  component: RouteComponent,
  validateSearch: (search) => questionsReviewSearchSchema.parse(search),
})

function RouteComponent() {
  const { quizId } = Route.useParams()
  const { quizstates } = Route.useSearch()
  const navigate = useNavigate()

  // Parse quiz state IDs from comma-separated string
  const quizStateIds = quizstates
    .split(",")
    .filter((id) => id.length > 0) as Id<"quizState">[]

  const quiz = useQuery(api.quizzes.handlers.getById, {
    quizId: quizId as Id<"quizzes">,
  })

  const questionsData = useQuery(
    api.questions.handlers.getQuestionsForReview,
    quizStateIds.length > 0
      ? {
          quizId: quizId as Id<"quizzes">,
          quizStateIds,
        }
      : "skip"
  )

  const batchMarkAsReviewed = useMutation(
    api.quizState.handlers.batchMarkQuizStatesAsReviewed
  )

  const overrideAnswerGrade = useMutation(
    api.answers.handlers.adminOverrideAnswerGrade
  )

  // Fetch active challenges
  const activeChallenges = useQuery(
    api.questions.handlers.getActiveChallengesForReview,
    quizStateIds.length > 0
      ? {
          quizId: quizId as Id<"quizzes">,
          quizStateIds,
        }
      : "skip"
  )

  // Calculate total active challenges
  const totalActiveChallenges = useMemo(() => {
    if (!questionsData) return 0
    return questionsData.reduce((sum, q) => sum + q.activeChallenges, 0)
  }, [questionsData])

  const handleCompleteReview = async () => {
    // Validate no unresolved challenges
    if (totalActiveChallenges > 0) {
      toast.error(
        `Cannot complete review: ${totalActiveChallenges} unresolved challenge(s) remaining.`
      )
      return
    }

    try {
      await batchMarkAsReviewed({ quizStateIds })
      // Navigate back to quiz detail page instances tab
      await navigate({
        to: "/admin/quizzes/$quizId",
        params: { quizId },
        search: { tab: "instances", status: "reviewed" },
      })
    } catch (error) {
      console.error("Failed to complete review:", error)
    }
  }

  const handleAcceptChallenge = async (answerId: Id<"answers">) => {
    try {
      await overrideAnswerGrade({ answerId, isCorrect: true })
      toast.success("Challenge accepted - answer marked as correct")
    } catch (error) {
      console.error("Failed to accept challenge:", error)
      toast.error("Failed to accept challenge")
    }
  }

  const handleRejectChallenge = async (answerId: Id<"answers">) => {
    try {
      await overrideAnswerGrade({ answerId, isCorrect: false })
      toast.success("Challenge rejected - answer remains incorrect")
    } catch (error) {
      console.error("Failed to reject challenge:", error)
      toast.error("Failed to reject challenge")
    }
  }

  if (quizStateIds.length === 0) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link
              to="/admin/quizzes/$quizId"
              params={{ quizId }}
              search={{ tab: "instances" }}
            >
              <ChevronLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Review Questions</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              No quiz states selected. Please go back and select quiz instances
              to review.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (quiz === undefined || questionsData === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (quiz === null) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="text-3xl font-bold">Quiz not found</h1>
      </div>
    )
  }

  const columns = createQuestionsColumns(quizId, quizstates)

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link
            to="/admin/quizzes/$quizId"
            params={{ quizId }}
            search={{ tab: "instances" }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{quiz.name} - Review Questions</h1>
          <p className="text-sm text-muted-foreground">
            Reviewing {quizStateIds.length} quiz instance(s)
          </p>
        </div>
        <Button
          onClick={() => void handleCompleteReview()}
          size="lg"
          disabled={totalActiveChallenges > 0}
        >
          Complete Review
        </Button>
      </div>

      {/* Warning banner if there are unresolved challenges */}
      {totalActiveChallenges > 0 && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <p className="text-amber-800 dark:text-amber-200">
            {totalActiveChallenges} unresolved challenge(s). Resolve all
            challenges before completing the review.
          </p>
        </div>
      )}

      {/* Active Challenges Section */}
      {activeChallenges && activeChallenges.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Active Challenges
              </CardTitle>
              <Badge variant="destructive">
                {activeChallenges.length} challenge(s)
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeChallenges.map((challenge) => (
                <div
                  key={challenge.answerId}
                  className="rounded-lg border p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          R{challenge.roundNumber}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {challenge.teamName || challenge.userName}
                        </span>
                      </div>
                      <p className="font-medium">
                        <SafeHtml html={challenge.questionText} />
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">
                            Their answer:
                          </span>
                          <p className="font-medium">{challenge.userAnswer}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Correct answer:
                          </span>
                          <p className="font-medium text-green-600">
                            <SafeHtml html={challenge.correctAnswer} />
                          </p>
                        </div>
                      </div>
                      {challenge.challengeReason && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Reason:</span>
                          <p className="italic">
                            "{challenge.challengeReason}"
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void handleAcceptChallenge(
                            challenge.answerId as Id<"answers">
                          )
                        }
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void handleRejectChallenge(
                            challenge.answerId as Id<"answers">
                          )
                        }
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Questions for Review</CardTitle>
            <Badge variant="secondary">
              {questionsData.length} question(s)
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={questionsData} />
        </CardContent>
      </Card>
    </div>
  )
}
